import { useState, useEffect, useCallback } from 'react';
import { track } from '@/lib/analytics';

const slides = [
  { src: '/landing/ES_IMG_Web_Wohnzimmer1_vorher.webp', alt: 'Original Immobilienbild', label: 'Original' },
  { src: '/landing/ES_IMG_Web_Wohnzimmer1_leer.webp', alt: 'Leer geräumt', label: 'Leer' },
  { src: '/landing/ES_IMG_Web_Wohnzimmer1_modern.webp', alt: 'Modern eingerichtet', label: 'Modern' },
  { src: '/landing/ES_IMG_Web_Wohnzimmer1_industrial.webp', alt: 'Industriell gestyled', label: 'Industriell' },
  { src: '/landing/ES_IMG_Web_Wohnzimmer1_luxus.webp', alt: 'Luxuriös eingerichtet', label: 'Luxuriös' },
];

const SLIDE_DURATION = 4000;

interface HeroSectionProps {
  onLoginClick?: () => void;
  onCtaClick?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onCtaClick }) => {
  const [activeSlide, setActiveSlide] = useState(2);
  const [tabKey, setTabKey] = useState(0); // force re-render of animation

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
    setTabKey(k => k + 1);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      goToSlide((activeSlide + 1) % slides.length);
    }, SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [activeSlide, goToSlide]);

  return (
    <div className="lp-hero-wrapper" id="main">
      <section className="lp-hero">
        <div className="lp-hero-content">
          <div>
            <div className="lp-hero-trust-badge">
              <img src="/landing/Estage_Logo_MadeinGermany2.png" alt="Made and Hosted in Germany" style={{ height: 32, width: 'auto' }} />
              <span>KI-Staging — Made &amp; Hosted in Germany</span>
            </div>
            <h1>Immobilien Staging<br />mit KI — in Sekunden</h1>
            <p className="lp-hero-description">
              Professionelle Immobilien Fotos beim Kaffee trinken? Räume leer räumen und stylisch einrichten – das geht mit Estage AI in Sekundenschnelle. Als Makler oder Hausverwalter bekommst du so wertvolle Zeit zurück, die du nicht mehr mit lästiger Bildbearbeitung verschwenden musst.
            </p>
            <div className="lp-hero-cta-wrapper">
              <button className="lp-btn-primary" onClick={() => { track('Landing CTA Clicked', { location: 'hero' }); onCtaClick?.(); }}>
                Jetzt kostenlos testen
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
                </svg>
              </button>
              <p className="lp-hero-cta-note">
                Registriere dich jetzt und erhalte <strong>10 Credits gratis</strong>.
              </p>
            </div>
          </div>

          <div className="lp-hero-partners">
            <div className="lp-hero-partners-logos">
              <div className="lp-hero-partners-logos-track">
                {/* set 1 */}
                <img src="/landing/partners/Logo_PNP-Invest.webp" alt="PNP Invest" className="lp-partner-logo-img" />
                <img src="/landing/partners/PNP-Media_Logo_quer_1.webp" alt="PNP Media" className="lp-partner-logo-img" />
                <img src="/landing/partners/Group_427319346.webp" alt="heimatmakler" className="lp-partner-logo-img" />
                <img src="/landing/partners/Logo_Made-in-Ger.webp" alt="Made in Germany" className="lp-partner-logo-img" />
                {/* set 2 (duplicate for seamless loop) */}
                <img src="/landing/partners/Logo_PNP-Invest.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
                <img src="/landing/partners/PNP-Media_Logo_quer_1.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
                <img src="/landing/partners/Group_427319346.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
                <img src="/landing/partners/Logo_Made-in-Ger.webp" alt="" aria-hidden="true" className="lp-partner-logo-img" />
              </div>
            </div>
          </div>
        </div>

        <div className="lp-hero-image-container">
          <div className="lp-hero-slides">
            {slides.map((slide, i) => (
              <div key={i} className={`lp-hero-slide${i === activeSlide ? ' active' : ''}`}>
                <img src={slide.src} alt={slide.alt} />
              </div>
            ))}
          </div>
          <div className="lp-hero-tabs">
            {slides.map((slide, i) => (
              <button
                key={`${i}-${tabKey}-${i === activeSlide}`}
                className={`lp-hero-tab${i === activeSlide ? ' active' : ''}`}
                onClick={() => goToSlide(i)}
              >
                <span className="lp-progress-fill" />
                <span>{slide.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
